/**
 * Subscription plans data — single source of truth for pricing across
 * the marketing page and in-app settings.
 *
 * Ported 1:1 from v1 SettingAccountSubscriptionPlans.ts
 */

export interface PriceWithLink {
  price: number
  link: string
}

export interface Pricing {
  monthly: PriceWithLink
  yearly: PriceWithLink
  biennially: PriceWithLink
}

export type PaymentTerm = "monthly" | "yearly" | "biennially"

export interface PropertyLabels {
  [key: string]: string
}

export const propertyLabels: PropertyLabels = {
  entries: "Number of tasks",
  backup: "Data backup",
  sync: "Synchronization",
  collaboration: "Collaboration",
  calendarIntegration: "Calendar integrations",
  mailToToToDo: "Email to To To-Do",
  devices: "Devices",
  images: "Images",
  sharableTemplates: "Sharable templates",
  aiRequests: "AI-requests",
}

export interface SubscriptionPlan {
  id: string
  name: string
  description?: string
  entries: "Unlimited"
  backup: "✓" | "-"
  sync: "✓" | "-"
  collaboration: "✓" | "-"
  calendarIntegration: "Unlimited" | "-"
  mailToToToDo: "✓" | "-"
  devices: "Unlimited" | number
  images: "Unlimited" | "-"
  sharableTemplates: "Unlimited" | "-"
  aiRequests: string
  pricing: Pricing
}

export const subscriptionPlans: SubscriptionPlan[] = [
  {
    id: "free",
    name: "Free",
    description:
      "For managing all aspects of your personal, family, and work life independently.",
    entries: "Unlimited",
    backup: "-",
    sync: "-",
    collaboration: "-",
    calendarIntegration: "-",
    mailToToToDo: "-",
    devices: 1,
    images: "-",
    sharableTemplates: "-",
    aiRequests: "-",
    pricing: {
      monthly: { price: 0, link: "" },
      yearly: { price: 0, link: "" },
      biennially: { price: 0, link: "" },
    },
  },
  {
    id: "standard",
    name: "Standard",
    description: "For getting family, work and personal life in sync.",
    entries: "Unlimited",
    backup: "✓",
    sync: "✓",
    collaboration: "✓",
    calendarIntegration: "Unlimited",
    mailToToToDo: "✓",
    devices: "Unlimited",
    images: "-",
    sharableTemplates: "-",
    aiRequests: "100 / month",
    pricing: {
      monthly: { price: 4.99, link: "" },
      yearly: { price: 53.88, link: "" },
      biennially: { price: 95.8, link: "" },
    },
  },
  {
    id: "premium",
    name: "Premium",
    description:
      "For complete management of your family, personal, and work life.",
    entries: "Unlimited",
    backup: "✓",
    sync: "✓",
    collaboration: "✓",
    calendarIntegration: "Unlimited",
    mailToToToDo: "✓",
    devices: "Unlimited",
    images: "Unlimited",
    sharableTemplates: "Unlimited",
    aiRequests: "1000 / month",
    pricing: {
      monthly: { price: 9.99, link: "" },
      yearly: { price: 107.892, link: "" },
      biennially: { price: 191.808, link: "" },
    },
  },
]

export const periods = [
  { key: "monthly" as const, label: "Monthly", discount: null },
  { key: "yearly" as const, label: "Yearly", discount: "Save 10%" },
  { key: "biennially" as const, label: "2 years", discount: "Save 20%" },
]

/** Format a per-month price from a total period price */
export function formatPerMonth(totalPrice: number, months: number): string {
  const perMonth = totalPrice / months
  return (
    perMonth.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " €/mo"
  )
}

/** Get display price for a plan+period */
export function getDisplayPrice(
  plan: SubscriptionPlan,
  period: PaymentTerm
): string {
  const p = plan.pricing[period]
  if (p.price === 0) return "Free"
  switch (period) {
    case "monthly":
      return formatPerMonth(p.price, 1)
    case "yearly":
      return formatPerMonth(p.price, 12)
    case "biennially":
      return formatPerMonth(p.price, 24)
  }
}

/** Get the discount badge text for a period, or null */
export function getDiscountBadge(period: PaymentTerm): string | null {
  switch (period) {
    case "yearly":
      return "-10%"
    case "biennially":
      return "-20%"
    default:
      return null
  }
}
